import React, { useState, useCallback, useRef, useEffect } from 'react';
import './PostReply.css';
import PostReplyInput from './PostReplyInput';
import LinkPreview from './LinkPreview';
import iconPostReplyModal from '../../../../assets/icons/icon_post_reply_modal.svg';
import icon_newpost_delete from '../../../../assets/icons/icon_newpost_delete.svg';
import icon_newpost_images from '../../../../assets/icons/icon_newpost_images.svg';
import icon_newpost_camera from '../../../../assets/icons/icon_newpost_camera.svg';
import icon_newpost_gif from '../../../../assets/icons/icon_newpost_gif.svg';
import { formatTimestamp, detectLink, convertLinkToTag } from '../../../common/utils';
import { UserType } from '../../../../types/userType';
import { getLinkPreview, LinkPreviewData } from '../../../../services/linkPreviewService';
import Camera from '../../../common/Camera/Camera';
import GiphyModal from '../../../common/GiphyModal/GiphyModal';
import VIToast from '../../../common/VIToast/VIToast';
import DOMPurify from 'dompurify';
import debounce from 'lodash/debounce';
import { createReplyPost } from '../../../../api/playgroundServices';
import { PostMedia, PostProps, ReplyPostData } from '../../../../types/postType';
import { applyCollabs } from '../../../../api/collabsServices';
import { useNavigate } from 'react-router-dom';
import MediaManager from './MediaManager';
import { processMediaFiles, processCameraCapture } from '../../../../services/mediaServices';
import VIModal from '../../../common/VIModal/VIModal';

type PostReplyType = 'reply' | 'collabsApply';

interface ReplyTarget {
  id: string | number;
  username: string;
  content: string;
}

interface PostReplyProps {
  isOpen: boolean;
  onClose: () => void;
  originalPost: PostProps;
  currentUser: UserType | null;
  onSubmit: (text: string, images: File[], gif: string | null) => void;
  replyingTo?: ReplyTarget;
  type?: PostReplyType;
  hideSubmitButton?: boolean;
  externalSubmitHandler?: () => void;
}

const PostReply: React.FC<PostReplyProps> = ({ isOpen, onClose, originalPost, currentUser, onSubmit, replyingTo, type = 'reply', hideSubmitButton = false, externalSubmitHandler }) => {
  const [replyContent, setReplyContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [mediaList, setMediaList] = useState<PostMedia[]>([]);
  const [linkPreview, setLinkPreview] = useState<LinkPreviewData | null>(null);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showGiphyModal, setShowGiphyModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (textAreaRef.current) {
        setTimeout(() => {
          if (textAreaRef.current) {
            textAreaRef.current.focus();
          }
        }, 100);
      }
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const debouncedDetectLink = useCallback(
    debounce(async (text: string): Promise<string | null> => {
      const link = detectLink(text);
      if (link) {
        try {
          const previewData = await getLinkPreview(link);
          setLinkPreview(previewData ? previewData as LinkPreviewData : null);
          return link;
        } catch (error) {
          console.error('Error fetching link preview:', error);
          setLinkPreview(null);
        }
      } else {
        setLinkPreview(null);
      }
      return null;
    }, 1000),
    []
  );

  const handleTextChange = async (newText: string) => {
    if (newText.length <= 1000) {
      setReplyContent(newText);
      const sanitizedText = DOMPurify.sanitize(newText);

      const detectedLink = await debouncedDetectLink(newText);

      let contentWithLinks = sanitizedText;
      if (detectedLink) {
        contentWithLinks = convertLinkToTag(detectedLink);
      }

      setHtmlContent(contentWithLinks);
    } else {
      setToastMessage('Reply is limited to 1000 characters.');
    }
  };

  const handleDelete = () => {
    setReplyContent('');
    setHtmlContent('');
    setMediaList([]);
    setLinkPreview(null);
    setSelectedGif(null);
    if (textAreaRef.current) {
      textAreaRef.current.value = '';
    }
  };

  const handleReplySubmit = async () => {
    if (!isSubmitEnabled || !currentUser) return;

    setIsPosting(true);

    try {
      if (type === 'collabsApply' && originalPost.collab?.id) {
        try {
          const response = await applyCollabs(
            originalPost.collab.id.toString(),
            replyContent,
            mediaList.map(media => media.file).filter(Boolean) as File[]
          );
          
          handleDelete();
          onClose();
          navigate('/successfully-applied');
          return;
        } catch (error: any) {
          if (error?.response?.data?.msg === "You have already applied to this collab") {
            setToastMessage("You have already applied to this collab");
            return;
          }
          throw error;
        }
      }

      // Normal reply logic
      const replyData: ReplyPostData = {
        id: Date.now(),
        content: replyContent,
        user: {
          id: currentUser.id.toString(),
          username: currentUser.username,
          avatar: currentUser.avatar
        },
        timestamp: new Date().toISOString(),
        parentPostId: originalPost.id,
        media: mediaList,
        postType: mediaList.length > 0 ? 'media' : 'text',
        currentUser: currentUser,
        currentUserId: currentUser.id.toString(),
        isNewPost: true,
        gif: selectedGif || undefined
      };
      await createReplyPost(replyData);
      handleDelete();
      onClose();
    } catch (error) {
      console.error('Error submitting:', error);
      setToastMessage(`Failed to ${type === 'collabsApply' ? 'submit application' : 'post reply'}. Please try again.`);
    } finally {
      setIsPosting(false);
    }
  };

  useEffect(() => {
    const hasEnoughInput = 
      replyContent.trim().length > 0 || 
      mediaList.length > 0 || 
      selectedGif !== null;
    
    setIsSubmitEnabled(hasEnoughInput);
  }, [replyContent, mediaList, selectedGif]);

  const handleClose = () => {
    if (!isPosting) {
      onClose();
    }
  };

  // Handle file selection
  const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("=== REPLY FILE SELECTION STARTED ===");
    
    const target = e.target;
    if (!target.files || target.files.length === 0) {
      console.log('No files selected');
      return;
    }
    
    console.log('Files selected:', target.files.length);
    
    try {
      // Use the refactored service function
      const { success, errors } = await processMediaFiles(target.files);
      
      if (errors.length > 0) {
        errors.forEach(error => setToastMessage(error));
      }
      
      if (success.length > 0) {
        setMediaList(prev => [...prev, ...success]);
      }
    } catch (error) {
      console.error("Error processing files:", error);
      setToastMessage("Error uploading media. Please try again.");
    }
    
    // Reset the file input value
    if (target) {
      target.value = '';
    }
    
    console.log("=== REPLY FILE SELECTION COMPLETED ===");
  };

  if (!isOpen) return null;

  return (
    <VIModal isOpen={isOpen} onClose={onClose}>
      <div className="post-reply-modal">
        <h2>{replyingTo ? `Reply to ${replyingTo.username}` : 'Add a Reply'}</h2>
        
        {replyingTo && (
          <div className="replying-to-preview">
            <p className="replying-to-username">{replyingTo.username}</p>
            <p className="replying-to-content">{replyingTo.content}</p>
          </div>
        )}
        
        <div className="post-reply-header">
          <button 
            className="cancel-button" 
            onClick={handleClose} 
            disabled={isPosting}
            style={{
              opacity: isPosting ? 0.5 : 1,
              cursor: isPosting ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent'
            }}
          >
            Cancel
          </button>
          <h2>{type === 'collabsApply' ? 'Apply to collab' : 'Reply'}</h2>
        </div>
        <div className="original-post">
          <div className="post-avatar-column">
            <img src={originalPost.user.avatar} alt={originalPost.user.username} className="post-avatar" />
            
          </div>
          <div className="post-content-column">
            <div className="post-header">
              <span className="post-username">{originalPost.user.username}</span>
              <span className="post-timestamp">{formatTimestamp(originalPost.timestamp)}</span>
            </div>
            <div className="post-content" dangerouslySetInnerHTML={{ __html: originalPost.content || '' }} />
            {originalPost.mediaList && originalPost.mediaList.length > 0 && (
              <div className="post-media-container">
                {originalPost.mediaList.map((media, index) => {
                  const isVideo = media.media_type === 'video' || 
                                 (media.url && (media.url.endsWith('.mp4') || media.url.endsWith('.mov')));
                  
                  return isVideo ? (
                    <video 
                      key={index}
                      src={media.url}
                      className="post-media"
                      controls
                      muted
                      playsInline
                      loop
                    />
                  ) : (
                    <img 
                      key={index} 
                      src={media.url} 
                      alt={`Post image ${index + 1}`} 
                      className="post-media" 
                    />
                  );
                })}
              </div>
            )}
            {originalPost.preview && originalPost.preview.url && (
              <LinkPreview
                url={originalPost.preview.url}
                title={originalPost.preview.title}
                description={originalPost.preview.description}
                image={originalPost.preview.image}
              />
            )}
          </div>
        </div>
        <div className="reply-icon-container">
          <img src={iconPostReplyModal} alt="Reply" className="reply-icon" />
        </div>
        <PostReplyInput
          content={replyContent}
          onContentChange={handleTextChange}
          currentUser={currentUser}
          originalPostUsername={originalPost.user.username}
          ref={textAreaRef}
          isClickable={false}
          placeholder={`Reply to ${originalPost.user.username}...`}
        />
        <MediaManager
          mediaList={mediaList}
          selectedGif={selectedGif}
          onMediaListChange={setMediaList}
          onGifChange={setSelectedGif}
          onError={setToastMessage}
          maxItems={10}
        />

        <div className="post-reply-footer">
          <div className="post-reply-actions">
            <input
              type="file"
              accept="image/*, video/mp4, video/quicktime"
              multiple
              onChange={handleFileSelection}
              style={{ display: 'none' }}
              id="reply-media-upload"
            />
            <label htmlFor="reply-media-upload" className="post-action-button">
              <img src={icon_newpost_images} alt="Upload Media" className="post-action-icon" />
            </label>
            <button className="post-action-button" onClick={() => setShowCamera(true)}>
              <img src={icon_newpost_camera} alt="Camera" className="post-action-icon" />
            </button>
            <button className="post-action-button" onClick={() => setShowGiphyModal(true)}>
              <img src={icon_newpost_gif} alt="GIF" className="post-action-icon" />
            </button>
          </div>
          {!hideSubmitButton && (
            <button
              className="post-submit-button"
              disabled={!isSubmitEnabled || isPosting}
              onClick={handleReplySubmit}
            >
              {isPosting ? 'Posting...' : type === 'collabsApply' ? 'Apply' : 'Reply'}
            </button>
          )}
        </div>
      </div>
      {showCamera && (
        <Camera
          onCapture={(imageSrc: string) => {
            const newMedia = processCameraCapture(imageSrc);
            setMediaList(prev => [...prev, newMedia]);
            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
      <GiphyModal
        isOpen={showGiphyModal}
        onSelect={(gifUrl) => {
          setSelectedGif(gifUrl);
          setShowGiphyModal(false);
        }}
        onClose={() => setShowGiphyModal(false)}
      />
      {toastMessage && (
        <VIToast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
    </VIModal>
  );
};

export default PostReply;
